import clsx from 'clsx'
import React, {FC, useMemo} from 'react'
import {usersAPI} from '../../../../services/UsersService'
import {IAppsModel} from '../../../../types/apps/AppsModel'
import {formatDateByZone} from '../../../../template/helpers/FormatDateZoneHelpers'
import {useIntl} from 'react-intl'
import {activeUsersCount, totalUsersCount} from '../../../../template/helpers/TotalUsers'

type Props = {
  app: IAppsModel
  firstElement: boolean
}

const AppItemBody: FC<Props> = ({app, firstElement}) => {
  const intl = useIntl()
  const {data: currentUser} = usersAPI.useFetchGetMeQuery()

  function dateTime(date: string) {
    return formatDateByZone(date, currentUser?.language || 'en', currentUser?.time_zone || 'UTC')
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeUsers = useMemo(() => activeUsersCount(app), [app])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const totalUsers = useMemo(() => totalUsersCount(app), [app])

  return (
    <div id={`cb_app_item_${app.id}`} className={clsx('fs-7 ps-12 collapse', {show: firstElement})}>
      <div className='d-flex flex-wrap py-5 ps-12'>
        <div className='flex-equal me-1'>
          <span className='fw-bolder text-gray-600 text-uppercase small'>
            {intl.formatMessage({id: 'APPS.BODY.TITLE.INTEGRATIONS'})}
          </span>
          <table className='table table-flush fw-bold gy-1 mt-4'>
            <tbody>
              <tr>
                <td className='fw-bolder text-gray-800 min-w-125px w-125px'>{intl.formatMessage({id: 'APPS.CRM'})}</td>
                <td className='fw-bolder text-gray-900'>
                  <span className='badge badge-light-primary'>
                    {app.crm === 'ONEC' ? <>1C</> : app.crm} / {app.crm_domain}
                  </span>
                </td>
              </tr>
              <tr>
                <td className='fw-bolder text-gray-800 min-w-125px w-125px'>{intl.formatMessage({id: 'APPS.PBX'})}</td>
                <td>
                  <span className='badge badge-light-warning me-2'>{app.pbx}</span>
                </td>
              </tr>
              <tr>
                <td className='fw-bolder text-gray-800 min-w-125px w-125px'>
                  {intl.formatMessage({id: 'APPS.VERSION'})}
                </td>
                <td>
                  <span className='badge badge-light-info'>{app.version}</span>
                </td>
              </tr>
              <tr>
                <td className='fw-bolder text-gray-800 min-w-125px w-125px'>
                  {intl.formatMessage({id: 'APPS.REGION'})}
                </td>
                <td>
                  {app.is_auto_mode && <span className='badge badge-light-danger me-2'>A</span>}
                  <span className='badge badge-light-info me-2'>{app.region}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className='flex-equal'>
          <span className='fw-bolder text-gray-600 text-uppercase small'>
            {intl.formatMessage({id: 'APPS.BODY.TITLE.LICENSE'})}
          </span>
          <table className='table table-flush fw-bold gy-1 mt-4'>
            <tbody>
              <tr>
                <td className='fw-bolder text-gray-800 min-w-125px w-125px'>
                  {intl.formatMessage({id: 'APPS.LICENSE.TYPE'})}
                </td>
                <td>
                  {app.licenses.is_commercial ? (
                    <span className='badge badge-success'>COMMERCIAL</span>
                  ) : (
                    <span className='badge badge-warning me-2'>TRIAL</span>
                  )}
                </td>
              </tr>
              <tr>
                <td className='fw-bolder text-gray-800 min-w-125px w-125px'>
                  {intl.formatMessage({id: 'APPS.LICENSE.STATUS'})}
                </td>
                <td>
                  {app.licenses.is_active ? (
                    <span className='badge badge-success me-2'>ACTIVE</span>
                  ) : (
                    <span className='badge badge-danger'>EXPIRED</span>
                  )}
                </td>
              </tr>
              {app.crm !== 'ROISTAT' && (
                <tr>
                  <td className='fw-bolder text-gray-800 min-w-125px w-125px'>
                    {intl.formatMessage({id: 'APPS.LICENSE.USERS'})}
                  </td>
                  <td className='fw-bolder text-gray-900'>
                    {intl.formatMessage({id: 'APPS.LICENSE.TOTAL'})} {app.licenses.maxusers} /{' '}
                    <span className={clsx({'text-danger': activeUsers > app.licenses.maxusers})}>
                      {intl.formatMessage({id: 'APPS.LICENSE.BODY.ACTIVE'})} {activeUsers} /{' '}
                    </span>
                    {intl.formatMessage({id: 'APPS.LICENSE.BODY.TOTAL'})} {totalUsers}
                  </td>
                </tr>
              )}
              <tr>
                <td
                  className={clsx(
                    'fw-bolder min-w-125px w-125px',
                    {'text-gray-800': app.licenses.is_active},
                    {'text-danger': !app.licenses.is_active}
                  )}
                >
                  {intl.formatMessage({id: 'APPS.LICENSE.END'})}
                </td>
                <td
                  className={clsx(
                    'fw-bolder',
                    {'text-gray-900': app.licenses.is_active},
                    {'text-danger': !app.licenses.is_active}
                  )}
                >
                  {dateTime(app.licenses.license_end)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export {AppItemBody}
